import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellOff, BellRing, Loader2, Send, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SwitchRow } from "@/components/switch-row";
import { api, ApiError } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { disableThisDevice, enablePush, notificationPermission, pushAvailability, syncThisDevice } from "@/lib/push";
import { deviceLabel } from "@/lib/push-utils";
import { qk } from "@/lib/query/keys";
import type { NotificationKind, NotificationPrefs, PushDevice } from "@/lib/types";

const KINDS: { kind: NotificationKind; label: string; hint: string }[] = [
  { kind: "waiting", label: "Waiting on you", hint: "An agent asks a question or is blocked on a card, or a card is ready for your review." },
  { kind: "agent_stopped", label: "Agent stopped", hint: "An agent's turn failed, so it is not doing what it was asked." },
  { kind: "card_resolved", label: "Card resolved", hint: "A card moves into a done column." },
  { kind: "agent_running", label: "Still running", hint: "An agent's turn is still going, every half hour or so, with how long it has run." },
];

const message = (error: unknown, fallback: string) => (error instanceof ApiError ? error.message : error instanceof Error ? error.message : fallback);

/**
 * Settings → Notifications (admins only: every notification is about agents). The
 * top half is this device — turning push on here asks the browser, from the tap — and
 * the other devices saved for this person; the switches below are per person, stored
 * on the server, so turning one off stops it on every device at once.
 */
export function NotificationsSettings() {
  const client = useQueryClient();
  const config = useQuery({ queryKey: qk.pushConfig(), queryFn: api.pushConfig, staleTime: Infinity });
  const prefs = useQuery({ queryKey: qk.notificationPrefs(), queryFn: () => api.notificationPrefs().then((r) => r.prefs) });
  const devices = useQuery({ queryKey: qk.pushDevices(), queryFn: () => api.pushDevices().then((r) => r.devices) });

  const availability = pushAvailability();
  const [permission, setPermission] = useState(notificationPermission);
  const [thisDevice, setThisDevice] = useState<PushDevice | null>(null);
  const [busy, setBusy] = useState<"enable" | "disable" | "test" | null>(null);

  // Which saved device is this browser (re-saving also heals a row removed elsewhere).
  useEffect(() => {
    if (!config.data?.publicKey) return;
    let live = true;
    void syncThisDevice()
      .then((device) => {
        if (!live) return;
        setThisDevice(device);
        if (device) void client.invalidateQueries({ queryKey: qk.pushDevices() });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [config.data?.publicKey, client]);

  const setPref = useMutation({
    mutationFn: (patch: Partial<NotificationPrefs>) => api.setNotificationPrefs(patch).then((r) => r.prefs),
    onMutate: async (patch) => {
      await client.cancelQueries({ queryKey: qk.notificationPrefs() });
      const previous = client.getQueryData<NotificationPrefs>(qk.notificationPrefs());
      if (previous) client.setQueryData<NotificationPrefs>(qk.notificationPrefs(), { ...previous, ...patch });
      return { previous };
    },
    onError: (error, _patch, context) => {
      if (context?.previous) client.setQueryData(qk.notificationPrefs(), context.previous);
      toast.error(message(error, "Could not save that"));
    },
    onSuccess: (next) => client.setQueryData(qk.notificationPrefs(), next),
  });

  const enable = async () => {
    const key = config.data?.publicKey;
    if (!key) return;
    setBusy("enable");
    try {
      const device = await enablePush(key);
      setPermission(notificationPermission());
      if (!device) {
        toast("Notifications were not allowed", { description: "Allow them for this site in the browser's settings, then try again." });
        return;
      }
      setThisDevice(device);
      toast.success("Notifications are on for this device");
    } catch (error) {
      toast.error(message(error, "Could not turn notifications on"));
    } finally {
      setBusy(null);
      void client.invalidateQueries({ queryKey: qk.pushDevices() });
    }
  };

  const disable = async () => {
    setBusy("disable");
    try {
      await disableThisDevice(thisDevice?.id ?? null);
      setThisDevice(null);
    } finally {
      setBusy(null);
      void client.invalidateQueries({ queryKey: qk.pushDevices() });
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const result = await api.testPush();
      if (result.sent === 0) toast.error("No device took the test", { description: "Turn notifications on for this device first." });
      else toast.success(`Test sent to ${result.sent} device${result.sent === 1 ? "" : "s"}`);
    } catch (error) {
      toast.error(message(error, "Could not send a test"));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (device: PushDevice) => {
    try {
      if (device.id === thisDevice?.id) await disable();
      else await api.removePushDevice(device.id);
    } catch (error) {
      toast.error(message(error, "Could not remove that device"));
    } finally {
      void client.invalidateQueries({ queryKey: qk.pushDevices() });
    }
  };

  if (config.isPending) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </p>
    );
  }

  const label = deviceLabel(navigator.userAgent, navigator.maxTouchPoints);
  const status: { text: string; tone?: "muted" | "warn"; action?: React.ReactNode } = !config.data?.publicKey
    ? { text: "Push isn't set up on this server yet (it needs VAPID keys).", tone: "muted" }
    : availability === "needs-install"
      ? { text: "Install to Home Screen to get notifications: in Safari tap Share, then “Add to Home Screen”, and open the app from there.", tone: "warn" }
      : availability === "unsupported"
        ? { text: "This browser can't receive notifications.", tone: "muted" }
        : thisDevice
          ? {
              text: "On. This device hears about the kinds switched on below.",
              action: (
                <>
                  <Button variant="outline" size="sm" disabled={busy !== null} onClick={() => void test()}>
                    {busy === "test" ? <Loader2 className="animate-spin" /> : <Send />} Send a test
                  </Button>
                  <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => void disable()}>
                    <BellOff /> Turn off
                  </Button>
                </>
              ),
            }
          : permission === "denied"
            ? { text: "Blocked in this browser. Allow notifications for this site in its settings, then come back.", tone: "warn" }
            : {
                text: "Off on this device.",
                action: (
                  <Button size="sm" disabled={busy !== null} onClick={() => void enable()}>
                    {busy === "enable" ? <Loader2 className="animate-spin" /> : <BellRing />} Enable on this device
                  </Button>
                ),
              };

  const list = devices.data ?? [];
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border/70 p-3">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium">This device · {label}</p>
            <p className={status.tone === "warn" ? "text-[12px] text-[var(--waiting)]" : "text-[12px] text-muted-foreground"}>{status.text}</p>
            {status.action ? <div className="mt-2.5 flex flex-wrap gap-2">{status.action}</div> : null}
          </div>
        </div>
      </div>

      {list.length > 0 ? (
        <div>
          <p className="mb-1.5 text-[12px] font-medium text-muted-foreground">Devices that get your notifications</p>
          <ul className="divide-y divide-border/60 rounded-md border border-border/70">
            {list.map((device) => (
              <li key={device.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate text-[13px]">
                    {device.label ?? "A browser"}
                    {device.id === thisDevice?.id ? <Badge variant="outline">This device</Badge> : null}
                  </p>
                  <p className="truncate text-[12px] text-muted-foreground">
                    Added {relativeTime(device.createdAt)}
                    {device.lastUsedAt ? ` · last notified ${relativeTime(device.lastUsedAt)}` : ""}
                    {device.failedAt ? " · last send failed" : ""}
                  </p>
                </div>
                <Button variant="ghost" size="icon" aria-label={`Remove ${device.label ?? "device"}`} onClick={() => void remove(device)}>
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="space-y-1">
        <p className="text-[12px] font-medium text-muted-foreground">Notify me when</p>
        {KINDS.map(({ kind, label: kindLabel, hint }) => (
          <SwitchRow
            key={kind}
            label={kindLabel}
            hint={hint}
            checked={prefs.data?.[kind] ?? true}
            onChange={(next) => setPref.mutate({ [kind]: next })}
          />
        ))}
      </div>
    </div>
  );
}
